/**
 * /home — the decisions of the signed-in front door, kept pure so they are tested without a
 * browser (src/components/HomePage.tsx renders them).
 *
 * The landing page sells Blitz to a stranger; a person with an account does not need selling,
 * they need the thing itself laid out: what is new since they were last here, what people liked,
 * every kind of card with a count, who is making things, and their own cards with the numbers.
 * That is a portal in the shape Newgrounds and Kongregate settled on — a live directory with the
 * maker's name on every tile — drawn for a phone, one column, rows that scroll sideways.
 *
 * Everything here reads card_public rows (the same columns the catalogue reads) and never a
 * rate: "Most liked" is the like count the catalogue already shows, "New" is created_at, and
 * nothing host-measured is ranked in public (docs/RANKING.md; STRATEGY section 9).
 */
import type { GameMeta } from './protocol';

/** A card_public row, as /home and the catalogue read it. */
export interface HomeRow {
  card_id: string;
  class: string;
  source: string;
  title: string;
  developer: string;
  page_url: string | null;
  thumbnail_url: string | null;
  accent: string | null;
  platform: string | null;
  source_url: string | null;
  media_url: string | null;
  embed_mode: string | null;
  created_at: string;
  likes: number;
}

/** The columns /home selects from card_public — the catalogue's list (public/catalogue/catalogue.js). */
export const HOME_COLUMNS = 'card_id,class,source,title,developer,page_url,thumbnail_url,accent,platform,source_url,media_url,embed_mode,created_at,likes';

export type KindKey = 'pages' | 'photos' | 'clips' | 'sites' | 'platforms' | 'toys' | 'games';

/**
 * The kinds, in the catalogue's order and with its titles (GROUPS in catalogue.js; the unit test
 * pins the two lists together): what people make with AI first, then what people link, then the
 * house's toys and games.
 */
export const KINDS: ReadonlyArray<{ key: KindKey; title: string; short: string }> = [
  { key: 'pages', title: 'Pages made with AI', short: 'Pages' },
  { key: 'photos', title: 'Images', short: 'Images' },
  { key: 'clips', title: 'Clips', short: 'Clips' },
  { key: 'sites', title: 'Sites people link', short: 'Sites' },
  { key: 'platforms', title: 'From the platforms', short: 'Platforms' },
  { key: 'toys', title: 'Toys', short: 'Toys' },
  { key: 'games', title: 'Games', short: 'Games' },
];

/** Which kind a row belongs to: the twin of groupOf in public/catalogue/catalogue.js, pinned by the unit test. */
export function kindOf(row: Pick<HomeRow, 'class' | 'source' | 'platform' | 'embed_mode' | 'page_url'>): KindKey {
  if (row.class === 'image') return 'photos';
  if (row.class === 'clip') return 'clips';
  if (row.source === 'wasm') return 'games';
  if (row.platform) return 'platforms';
  if (row.embed_mode === 'mirror' || row.source === 'external') return 'sites';
  if (row.source === 'hosted' && typeof row.page_url === 'string' && row.page_url.startsWith('/cards/html/')) return 'pages';
  if (row.source === 'hosted') return 'toys';
  return 'sites';
}

const time = (iso: string): number => {
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : 0;
};

/** The newest `n` cards. */
export function newest(rows: HomeRow[], n: number): HomeRow[] {
  return [...rows].sort((a, b) => time(b.created_at) - time(a.created_at) || a.card_id.localeCompare(b.card_id)).slice(0, Math.max(0, n));
}

/** Added within the last `days` days of `now`. */
export function isRecent(row: Pick<HomeRow, 'created_at'>, now: number, days = 7): boolean {
  const t = time(row.created_at);
  return t > 0 && now - t < days * 86_400_000;
}

/** The most liked `n` cards, the newest first among equals; cards nobody liked are not "most liked". */
export function mostLiked(rows: HomeRow[], n: number): HomeRow[] {
  return rows
    .filter((r) => Number(r.likes) > 0)
    .sort((a, b) => Number(b.likes) - Number(a.likes) || time(b.created_at) - time(a.created_at) || a.card_id.localeCompare(b.card_id))
    .slice(0, Math.max(0, n));
}

/** Every kind with at least one card, in KINDS order, with its count. */
export function kindCounts(rows: HomeRow[]): Array<{ key: KindKey; title: string; short: string; count: number }> {
  const by = new Map<KindKey, number>();
  for (const r of rows) by.set(kindOf(r), (by.get(kindOf(r)) ?? 0) + 1);
  return KINDS.map((k) => ({ ...k, count: by.get(k.key) ?? 0 })).filter((k) => k.count > 0);
}

/** Someone with live cards under their handle. */
export interface Maker {
  handle: string;
  count: number;
  /** The thumbnail of their newest card, for the tile. */
  thumb: string | null;
  /** When their newest card was added. */
  latest: string;
}

/**
 * The people on Blitz: handles over cards that are not a platform's (a harvested sketch carries
 * its platform author's name, not a Blitz handle — card_public has no other way to tell the two
 * apart), most cards first, then the most recent. Titles and handles are user text; the caller
 * renders them as text.
 */
export function makers(rows: HomeRow[], n: number): Maker[] {
  const by = new Map<string, Maker>();
  for (const r of newest(rows, rows.length)) {
    if (r.platform || !r.developer) continue;
    const handle = r.developer.trim();
    if (!handle) continue;
    const m = by.get(handle);
    if (m) m.count += 1;
    else by.set(handle, { handle, count: 1, thumb: r.thumbnail_url || null, latest: r.created_at });
  }
  return [...by.values()].sort((a, b) => b.count - a.count || time(b.latest) - time(a.latest) || a.handle.localeCompare(b.handle)).slice(0, Math.max(0, n));
}

const VIA = /^[a-z0-9_-]{1,32}$/;
const USER_ID = /^[a-z0-9][a-z0-9_-]{0,80}$/i;

/**
 * The attribution every door on a page carries, as the landing passes it through: the page's own
 * tag (`own`: 'home', 'maker') unless the visit arrived tagged — a pilot's ?via=pilot, a DM's
 * ?via=dm — in which case the link's tag stays, plus a valid ?r= referrer. Each value must pass
 * the app's own check (src/lib/share.ts: VIA and USER_ID) or it is dropped, as the feed would.
 */
export function attribution(search: string, own: string): string {
  const q = new URLSearchParams(search);
  const out = new URLSearchParams();
  const via = q.get('via');
  const r = q.get('r');
  out.set('via', via && VIA.test(via) ? via : own);
  if (r && USER_ID.test(r)) out.set('r', r);
  return `?${out.toString()}`;
}

/** A card's door: the share link, which opens the feed positioned on it, with the page's attribution. */
export function cardHref(cardId: string, attribution: string): string {
  return `/c/${encodeURIComponent(cardId)}${attribution}`;
}

/** A maker's page. Only a handle the server could have issued ([a-z0-9_]{3,20}) gets one. */
export function makerHref(handle: string): string | null {
  return /^[a-z0-9_]{3,20}$/.test(handle) ? `/@${handle}` : null;
}

/** The handle a /@<handle> path asks for, or null when the path is not one. */
export function handleFromPath(pathname: string): string | null {
  const m = /^\/@([a-z0-9_]{3,20})\/?$/i.exec(pathname);
  return m ? m[1].toLowerCase() : null;
}

/** "today", "yesterday", "3 days ago", "2 weeks ago", then the month and day. */
export function ago(iso: string, now: number): string {
  const t = time(iso);
  if (!t) return '';
  const days = Math.floor((now - t) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 28) return `${Math.floor(days / 7)} week${days < 14 ? '' : 's'} ago`;
  return new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** A bundled card (the build with no backend) in the shape card_public would give it, so /home has something to show. */
export function metaToRow(meta: GameMeta, created_at = '2026-09-26T09:00:00Z'): HomeRow {
  return {
    card_id: meta.id,
    class: meta.class ?? (meta.kind === 'web' ? 'web' : 'solo'),
    source: meta.source ?? (meta.kind === 'web' ? 'hosted' : 'wasm'),
    title: meta.title,
    developer: meta.developer,
    page_url: meta.page_url ?? null,
    thumbnail_url: meta.thumbnail_url || null,
    accent: meta.accent ?? null,
    platform: meta.platform ?? null,
    source_url: meta.source_url ?? null,
    media_url: meta.media_url ?? null,
    embed_mode: meta.embed_mode ?? null,
    created_at,
    likes: 0,
  };
}
